// Parser de nomes de canal (Xtream raw → estruturado).
// Detecta qualidade, codec, idioma, tags. Limpa o nome pra agrupamento de variantes.
// Ver §5F do PLAN.md.
import { CURATED_KEY_PREFIX, ISOLATED_KEY_PREFIX, UNNAMED_KEY_PREFIX } from './family-keys';
const QUALITY_PATTERNS = [
    { q: '4K', rx: /\b(4K|UHD|2160p?)\b/i },
    { q: 'FHD', rx: /\b(FHD|FULL\s?HD|1080p?)\b/i },
    { q: 'HD', rx: /\b(HD|720p?)\b/i },
    { q: 'SD', rx: /\b(SD|480p?)\b/i },
];
const CODEC_PATTERNS = [
    { c: 'H265', rx: /\b(H\.?265|HEVC|x265)\b/i },
    { c: 'H264', rx: /\b(H\.?264|AVC|x264)\b/i },
];
const LANGUAGE_PATTERNS = [
    { lang: 'pt-BR', rx: /\b(BR|BRASIL|PT[-\s]?BR|PT\s?BR|PORT)\b/i },
    { lang: 'pt-PT', rx: /\b(PT[-\s]?PT|PORTUGAL)\b/i },
    { lang: 'es', rx: /\b(LATINO|LAT|HISPANO|ESPAÑOL|ESPANHOL)\b/i },
    { lang: 'en', rx: /\b(EN|ENG|INGL[ÊE]S|US|UK|USA)\b/i },
    { lang: 'fr', rx: /\b(FR|FRENCH|FRANCES)\b/i },
    { lang: 'it', rx: /\b(IT|ITAL[IY]A?N?)\b/i },
];
const AUDIO_PATTERNS = [
    { audio: 'dubbed', rx: /\b(DUB|DUBLADO|DUBBED)\b/i },
    { audio: 'subbed', rx: /\b(LEG|LEGENDADO|SUB|SUBBED|SUBT[IÍ]TUL\w*)\b/i },
    { audio: 'original', rx: /\b(ORIG|ORIGINAL|VO)\b/i },
];
// `+` SOLTO (com espaço antes) = canal fallback ("Globo +", "SBT +").
// `+` COLADO (Disney+, ESPN+, Discovery+) = parte do nome, NÃO fallback.
// PLUS como palavra NÃO é tratado como fallback automaticamente — vira muito
// falso-positivo (HBO Plus, Discovery Plus, ESPN Plus são canais legítimos).
const PLUS_FALLBACK_RX = /(?:\s|^)\+(?=\s|$)/;
const TAG_PATTERNS = [
    { tag: 'backup', rx: /\bBACKUP\b/i },
    { tag: 'plus', rx: PLUS_FALLBACK_RX },
    { tag: 'test', rx: /\b(TEST|TESTE)\b/i },
    { tag: 'vip', rx: /\bVIP\b/i },
];
// ===== Memoização (perf TV) =====
// parseChannelName roda ~11 regex de detecção + ~10 de limpeza POR canal. Em
// TV ao Vivo o groupLiveChannels é chamado 2× por render sobre os mesmos ~1.041
// canais (allGroupsCache no cru + groupedBase no ordenado): os MESMOS rawName
// eram re-parseados. Cache por rawName colapsa a 2ª passada e torna re-aberturas
// da tela ~gratuitas (mesmas strings). Função é pura → cache é sempre correto.
// Saída DEVE ser tratada como imutável (callers só leem — inclusive `tags`).
// Cap com evição FIFO (Map preserva ordem de inserção): teto de memória em
// Tizen 2017 e no backend de longa duração (parse compartilhado entre catálogos).
const PARSE_CACHE_MAX = 5000;
const parseCache = new Map();
/**
 * ===== Robustez a dado sujo (o provedor Xtream não respeita o nosso type) =====
 *
 * O type system promete `name: string`; o provedor entrega `null`, campo
 * ausente ou número sem aspas. O app de TV lê o payload CRU
 * (`apps/tv/src/api.ts#getChannels` não faz `parse`/`safeParse`), então um
 * `raw.trim()` sem guarda era TypeError fora de render → o error boundary
 * comia LiveScreen/EpgScreen inteiras, porque o agrupamento roda ANTES da
 * ordenação já blindada (`sortChannels`).
 *
 * CUIDADO com o atalho "na API o zod já filtrou". Ele é meia-verdade e custou
 * uma regressão de superfície no painel: `XtreamLiveStreamSchema` declara
 * `name: z.string()` **sem `.min(1)`** (xtream-types.ts). Logo:
 *   - `null` / ausente / não-string → o `safeParse` DESCARTA o item, e o
 *     crash acima é mesmo exclusivo da TV;
 *   - `""` e `"   "` → **passam inteiros** e chegam aqui pelos dois caminhos.
 * Ou seja, "canal sem nome utilizável" NÃO é um problema só de TV. Por isso
 * `groupChannels`/`groupChannelsGlobal` também numeram os sem-nome
 * (`labelUnnamedGroups`), e não só `finalizeGroups` do lado da TV.
 *
 * Política IDÊNTICA à de `apps/tv/src/lib/sort-alpha.ts#sortKeyOf` — o app não
 * pode ter duas políticas pra mesma coisa. `String(v)` seria a correção
 * "óbvia" e está ERRADA (medido em Node 22):
 *   - `String(Object.create(null))` → TypeError; `String({ toString(){ throw } })`
 *     propaga o throw. A correção ingênua troca um crash por outro.
 *   - `String(Symbol('x'))` não lança, mas devolve "Symbol(x)" — nome lixo que
 *     viraria um canal fantasma chamado "Symbol(x)" na grade.
 * Daí a whitelist explícita, sem coerção cega: só string e número finito.
 *
 * ÚNICA divergência de `sortKeyOf`: aqui NÃO se faz trim. `sortKeyOf` produz
 * chave de ORDENAÇÃO (trim faz parte da chave); aqui a string alimenta o campo
 * `Parsed.raw`, que é o texto de entrada verbatim, e o trim acontece logo
 * abaixo em `computeParsedName`. Pra entrada string, isto é a IDENTIDADE —
 * nenhum nome válido muda de resultado.
 */
export function parsableNameOf(raw) {
    if (typeof raw === 'string')
        return raw;
    // Xtream devolve campo numérico sem aspas (canal "2024" chega 2024).
    // `Number.isFinite` barra NaN/Infinity, que virariam o nome "NaN"/"Infinity".
    if (typeof raw === 'number' && Number.isFinite(raw))
        return String(raw);
    // null, undefined, boolean, objeto, array, Symbol, NaN → sem nome utilizável.
    return '';
}
/**
 * Mesma whitelist do `parsableNameOf`, aplicada ao `id` (o `stream_id` do
 * Xtream). Devolve `''` quando não há id utilizável.
 *
 * Por que o id precisa da MESMA disciplina que o nome: a guarda de nome sozinha
 * não fecha a classe. Quando o nome sumia, a chave do grupo virava
 * `` `__unnamed__::${ch.id}` `` — e com `ch.id` também sujo isso interpola pra a
 * string LITERAL `'__unnamed__::undefined'`, idêntica pra todos os canais
 * quebrados. Ou seja: o colapso que a guarda de nome desfez voltava inteiro
 * assim que o segundo campo vinha sujo. Medido: 3 canais `{id:undefined,
 * name:null}` → 1 grupo. Interpolar dado não-validado dentro de uma chave é a
 * armadilha — o template string coage QUALQUER coisa em texto e o resultado
 * parece uma chave legítima.
 *
 * `id: 0` é preservado (`'0'`): zero é um stream_id válido e não pode cair no
 * mesmo balde de `undefined`.
 */
export function parsableIdOf(raw) {
    if (typeof raw === 'string')
        return raw;
    if (typeof raw === 'number' && Number.isFinite(raw))
        return String(raw);
    return '';
}
/**
 * `raw` é `unknown` DE PROPÓSITO: o tipo `string` era uma promessa que o
 * provedor não cumpre, e declarar a verdade obriga a normalização em vez de
 * escondê-la. Caller que já passa `string` continua compilando igual.
 */
export function parseChannelName(raw) {
    // Normaliza ANTES do cache: mantém a chave do Map sempre string. Com a chave
    // crua, um `null`/objeto nunca daria hit (objeto é comparado por referência)
    // e ainda ocuparia slot do cap FIFO — puro desperdício em catálogo grande.
    const key = parsableNameOf(raw);
    const cached = parseCache.get(key);
    if (cached !== undefined)
        return cached;
    const result = computeParsedName(key);
    if (parseCache.size >= PARSE_CACHE_MAX) {
        const oldest = parseCache.keys().next().value;
        if (oldest !== undefined)
            parseCache.delete(oldest);
    }
    parseCache.set(key, result);
    return result;
}
/**
 * Recebe a string JÁ normalizada por `parsableNameOf` (nunca null/não-string).
 *
 * Contrato pra entrada vazia (`''` — nome ausente, sujo, ou só espaços): NÃO
 * lança e devolve o resultado neutro `{ raw:'', cleanName:'', quality:null,
 * codec:null, language:null, audio:null, tags:[] }`. Cai naturalmente: nenhuma
 * regex de detecção casa `''`, e o fallback "se ficou vazio usa o raw" devolve
 * `''` de novo. Quem agrupa trata `cleanName === ''` como "sem evidência de
 * identidade" — ver `accumulateChannel` em channel-grouping.ts.
 */
function computeParsedName(raw) {
    const trimmed = raw.trim();
    // Detecta qualidade (primeira que matchar ganha — ordem 4K → SD)
    let quality = null;
    for (const { q, rx } of QUALITY_PATTERNS) {
        if (rx.test(trimmed)) {
            quality = q;
            break;
        }
    }
    // Codec
    let codec = null;
    for (const { c, rx } of CODEC_PATTERNS) {
        if (rx.test(trimmed)) {
            codec = c;
            break;
        }
    }
    // HEVC/H265 implica que NÃO é H264 padrão — explicit
    if (!codec && quality === '4K')
        codec = 'H265'; // 4K geralmente é HEVC
    // Idioma
    let language = null;
    for (const { lang, rx } of LANGUAGE_PATTERNS) {
        if (rx.test(trimmed)) {
            language = lang;
            break;
        }
    }
    // Áudio
    let audio = null;
    for (const { audio: a, rx } of AUDIO_PATTERNS) {
        if (rx.test(trimmed)) {
            audio = a;
            break;
        }
    }
    // Tags
    const tags = [];
    for (const { tag, rx } of TAG_PATTERNS) {
        if (rx.test(trimmed))
            tags.push(tag);
    }
    // Nome limpo: remove tudo que detectamos
    let clean = trimmed;
    // Remove brackets/colchetes inteiros
    clean = clean.replace(/[[(]\s*[^[\]()]*\s*[\])]/g, ' ');
    // Pipes (`|`) separam partes do nome em vários providers:
    // - "TV GAZETA | VITORIA HD"  → quero "TV GAZETA VITORIA" (afiliada regional)
    // - "CASA DO PATRAO | MULTICAM 01" → quero "CASA DO PATRAO MULTICAM 01"
    // - "00:50 | Mineros x Tepatitlán" → quero "Mineros x Tepatitlán" (hora é só
    //   horário do evento, não faz parte do nome do canal)
    // Estratégia: se o lado esquerdo do pipe é SÓ hora (HH:MM), descarta o lado
    // esquerdo. Caso contrário junta ambos com espaço.
    clean = clean.replace(/\s*\|\s*/g, (_match) => '|');
    clean = clean
        .split('|')
        .map((part) => part.trim())
        .filter((part) => {
        // Descarta partes que são só hora (HH:MM ou H:MM)
        if (/^\d{1,2}:\d{2}$/.test(part))
            return false;
        return part.length > 0;
    })
        .join(' ');
    // Remove qualidade/codec textual
    clean = clean.replace(/\b(4K|UHD|2160p?|FHD|FULL\s?HD|1080p?|HD|720p?|SD|480p?|H\.?265|HEVC|x265|H\.?264|AVC|x264)\b/gi, '');
    // Remove tags posicionais comuns: BACKUP, vip, test, alt, alt#, opt#, fonte#
    clean = clean.replace(/\b(BACKUP|VIP|TEST|TESTE|ALT\s*\d*|OPT\s*\d*|FONTE\s*\d*)\b/gi, '');
    // Remove `+` solto (com espaço) — fallback de fonte.
    // PRESERVA `Disney+`, `ESPN+`, `Apple TV+` (sem espaço antes do +).
    // PLUS palavra NÃO é removido (HBO Plus, Discovery Plus etc. são canais legítimos).
    clean = clean.replace(/(?:\s|^)\+(?=\s|$)/g, ' ');
    // Remove ordinais sobrescrito (¹²³⁴⁵⁶⁷⁸⁹⁰ → variante de fonte/backup, "A&E ²" → "A&E").
    // ATENÇÃO: NÃO remover número normal no fim. "PFC 1", "SporTV 2", "Globo News 4"
    // são canais distintos, não variantes de qualidade.
    clean = clean.replace(/[¹²³⁴⁵⁶⁷⁸⁹⁰]+/g, '');
    // Remove ano (1900-2099) do começo do nome — comum em torneios:
    // "2026 Turkish Airlines Open" → "Turkish Airlines Open".
    // Conservador: só no começo, só 4 dígitos, exige espaço depois.
    clean = clean.replace(/^(19|20)\d{2}\s+/, '');
    // Remove caracteres separadores soltos no fim/começo (/, -, –, —, ·, ., :)
    clean = clean
        .replace(/[\s/\-–—·.:|]+$/g, '')
        .replace(/^[\s/\-–—·.:|]+/g, '');
    // Múltiplos espaços
    clean = clean.replace(/\s+/g, ' ').trim();
    // Se ficou vazio (canal só tem qualidade), usa o raw
    if (!clean)
        clean = trimmed;
    return {
        raw,
        cleanName: clean,
        quality,
        codec,
        language,
        audio,
        tags,
    };
}
/**
 * Garante a MESMA invariante que `finalizeGroups` garante do lado da TV:
 * nenhum grupo sai com `cleanName` vazio. Aplicada como pós-passe porque
 * durante o loop o `cleanName` ainda participa da decisão de canonical
 * ("o mais curto vence") — numerar antes envenenaria essa comparação.
 *
 * Isto NÃO é simetria decorativa. A premissa de que "no caminho da API o zod
 * já descartou o item sem nome" é FALSA pra string vazia: o
 * `XtreamLiveStreamSchema` declara `name: z.string()` SEM `.min(1)`
 * (xtream-types.ts), então `""` e `"   "` atravessam o `safeParse` inteiros.
 * Sem este pós-passe, `GET /admin/v1/live/families` devolvia N linhas com
 * `displayName: ''`, todas ordenadas em PRIMEIRO (`''.localeCompare` vence
 * tudo, admin-family.ts) e todas caindo na aba "Sem família"
 * (`memberCount === 1 && !curated`) — que é justamente a tela onde o curador
 * monta família. Dar chave própria a cada sem-nome trocou 1 linha colapsada
 * por N linhas em branco: sem numerar, a aba fica ilegível.
 */
function labelUnnamedGroups(groups) {
    let seq = 0;
    for (const g of groups.values()) {
        if (g.cleanName)
            continue;
        seq += 1;
        g.cleanName = String(seq);
        g.unnamedOrdinal = seq;
    }
    return groups;
}
export function groupChannels(channels) {
    const groups = new Map();
    for (const ch of channels) {
        // Buraco no array (item null vindo de página ruim / flatMap do provedor):
        // não existe canal pra agrupar, e ler `ch.rawName` derrubaria a lista
        // INTEIRA por causa de um slot. Mesma decisão que `groupLiveChannelsChunked`
        // já tomava pro seu loop — ver channel-grouping.ts.
        if (!ch)
            continue;
        // Sem id utilizável não existe canal: o id é a identidade (e o que o player
        // abre). `if (ch)` NÃO cobre isto — `{}` é truthy e passava direto, virando
        // a chave `'__unnamed__::undefined'` compartilhada por todos os quebrados.
        // Ver `parsableIdOf` e a nota de `accumulateChannel` (channel-grouping.ts).
        const id = parsableIdOf(ch.id);
        if (!id)
            continue;
        const parsed = parseChannelName(ch.rawName ?? ch.name);
        // Sem nome utilizável não há evidência NENHUMA de identidade compartilhada;
        // agrupar por `cat::` colapsaria canais sem relação num grupo só. Ver a
        // nota longa em `accumulateChannel` (channel-grouping.ts) — é a mesma
        // política, e o princípio declarado do módulo é "na dúvida, NÃO agrupa".
        const key = parsed.cleanName
            ? `${ch.categoryId}::${parsed.cleanName.toLowerCase()}`
            : `${UNNAMED_KEY_PREFIX}${id}`;
        let g = groups.get(key);
        if (!g) {
            g = {
                key,
                cleanName: parsed.cleanName,
                categoryId: ch.categoryId,
                variants: [],
            };
            groups.set(key, g);
        }
        g.variants.push({
            id,
            quality: parsed.quality,
            codec: parsed.codec,
            language: parsed.language,
            audio: parsed.audio,
            tags: parsed.tags,
        });
    }
    return labelUnnamedGroups(groups);
}
/**
 * Normaliza nome pra chave de agrupamento. Strip de acentos (NFD) +
 * lowercase + collapse de espaços. Necessário porque providers gravam
 * o mesmo canal com variações: "TNT Series" vs "TNT Séries", "Discovery"
 * vs "Discóvery". cleanName original é preservado pra exibição.
 */
function normalizeGroupKey(s) {
    return s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // diacríticos combining
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Agrupa canais GLOBALMENTE. Algoritmo em 2 passes:
 *
 * 1º pass — agrupa por **epgChannelId** (autoritário): mesmo EPG → mesmo
 *    canal logicamente. Resolve "AXN" vs "AXN Legendado" (ambos epg=AXN),
 *    "TNT Series" vs "TNT Séries" (ambos epg=TNTSeries), etc.
 * 2º pass — agrupa por cleanName normalizado os que NÃO tinham epgChId.
 *
 * Quando 2 grupos do 1º pass têm o mesmo epgChannelId mas variações de
 * cleanName ("AXN" + "AXN Legendado"), o cleanName mais curto vence
 * (é o canonical sem sufixos).
 */
export function groupChannelsGlobal(channels) {
    const groups = new Map();
    // index auxiliar: epgChId → key do groupMap (pra reuso entre canais
    // de cleanName diferentes mas mesmo EPG)
    const epgToKey = new Map();
    for (const ch of channels) {
        // Slot vazio no array: ver a mesma nota em `groupChannels` acima.
        if (!ch)
            continue;
        // Sem id utilizável não existe canal — mesma guarda de `groupChannels`.
        const id = parsableIdOf(ch.id);
        if (!id)
            continue;
        const parsed = parseChannelName(ch.rawName ?? ch.name);
        const cleanKey = normalizeGroupKey(parsed.cleanName);
        const epgChId = ch.epgChannelId ?? null;
        // Curadoria de família (override) vence a heurística + o EPG:
        //   - familyIsolated: não agrupa com ninguém → key único (id).
        //   - familyKey: identidade curada → agrupa quem compartilha o key.
        //   - sem override: EPG (autoritário) > cleanName (comportamento de hoje).
        let key;
        if (ch.familyIsolated) {
            key = `${ISOLATED_KEY_PREFIX}${id}`;
        }
        else if (ch.familyKey) {
            key = `${CURATED_KEY_PREFIX}${ch.familyKey}`;
            // Registra o EPG → esta família, pra que um IRMÃO de mesmo EPG sem
            // override caia nesta família (não forme grupo próprio pelo cleanName).
            if (epgChId && !epgToKey.has(epgChId))
                epgToKey.set(epgChId, key);
        }
        else if (epgChId && epgToKey.has(epgChId)) {
            key = epgToKey.get(epgChId);
        }
        else {
            // `cleanKey` vazio = nome inutilizável. Sem override e sem EPG não há
            // evidência de identidade, então o canal vira grupo PRÓPRIO em vez de
            // colapsar todos os sem-nome num único grupo `''`.
            key = cleanKey || `${UNNAMED_KEY_PREFIX}${id}`;
            // O EPG é autoritário e ancora mesmo sem nome: dois canais sem nome com
            // o MESMO epgChannelId são o mesmo canal, e têm que agrupar (há teste).
            //
            // RESÍDUO CONHECIDO, registrado no changelog: quando o sem-nome chega
            // ANTES dos irmãos (a ordem do Xtream é arbitrária), a família fica
            // ancorada numa chave `__unnamed__::<id>`, que `isSyntheticFamilyKey`
            // rejeita — um grupo de 2+ membros com chave que o DELETE by-key recusa.
            // Hoje é inalcançável pela UI (o painel só oferece desfazer pra família
            // `curated`, e esta nasce `curated:false`), e o RÓTULO já está correto
            // (o `!g.cleanName ||` abaixo recupera o nome do irmão nomeado). Fechar
            // de vez exige um formato de chave novo pra família-por-EPG, que é
            // decisão de contrato com o painel — não cabe num fix.
            if (epgChId)
                epgToKey.set(epgChId, key);
        }
        let g = groups.get(key);
        if (!g) {
            g = {
                key,
                // displayName curado (renomear) > cleanName da heurística.
                cleanName: ch.familyName ?? parsed.cleanName,
                categoryId: ch.categoryId, // do primeiro encontrado
                variants: [],
            };
            groups.set(key, g);
        }
        else if (ch.familyName) {
            // override de nome sempre vence o display do grupo.
            g.cleanName = ch.familyName;
        }
        else if (parsed.cleanName && (!g.cleanName || parsed.cleanName.length < g.cleanName.length)) {
            // Sem override de nome: cleanName mais curto (canonical sem sufixo
            // "Legendado") vira display. Heurística: nome canônico é o mais curto.
            //
            // Duas guardas, e as duas são necessárias — a comparação de comprimento
            // sozinha é ORDEM-DEPENDENTE nos dois sentidos:
            //   `parsed.cleanName &&` impede o vazio de APAGAR um nome já bom
            //     (`''.length < 'Globo'.length` → true).
            //   `!g.cleanName ||` RECUPERA o grupo que nasceu vazio quando um irmão
            //     nomeado chega depois (`'Globo'.length < ''.length` → false PRA
            //     SEMPRE, então sem isto o grupo ficava sem rótulo eternamente).
            // Como a ordem da lista do provedor é arbitrária, cobrir só um sentido
            // é cobrir metade da classe.
            g.cleanName = parsed.cleanName;
        }
        g.variants.push({
            id,
            quality: parsed.quality,
            codec: parsed.codec,
            language: parsed.language,
            audio: parsed.audio,
            tags: parsed.tags,
        });
    }
    return labelUnnamedGroups(groups);
}
//# sourceMappingURL=name-parser.js.map